// 상단 가격 차트 (매매/전세) — variant='sale' | 'lease'
import React, { useMemo } from 'react';
import ChartCard from './ChartCard';
import {
  CHART_METRICS, METRIC_COLORS, METRIC_TOGGLES, STAR_SYMBOL_PATH,
  SALE_MAIN_METRICS, LEASE_MAIN_METRICS,
  TOP_TOGGLE_KEYS, buildTopToggleGroups, TOP_BRUSH, TOP_OPTION_PREFIX,
} from './constants';
import {
  toNumber, toDateKey, toTimestamp, darkenHex, fillContinuousValues,
  getEndLabelStyle, getDealTypeLabel, buildPaddedAxisRange, buildSyncedDataZoom,
  formatPercent, formatKrwInManWonShort, formatKrwEokFromManwon, safeTooltipValueFormatter,
} from './utils';

const MAIN_METRICS = { sale: SALE_MAIN_METRICS, lease: LEASE_MAIN_METRICS };

// 최근8주 시작 시각(최소 baseDate) — 실거래 라인을 이 시점부터 최근8주 값으로 대체하기 위함
const recent8StartTs = (recent8Rows) => {
  let startTs = Infinity;
  (recent8Rows || []).forEach((row) => {
    const price = toNumber(row?.price);
    const date = toDateKey(row?.baseDate);
    if (date && price != null) {
      const t = toTimestamp(date);
      if (t < startTs) startTs = t;
    }
  });
  return startTs;
};

// 축 포인터(마우스 크로스헤어) 라벨용: 만원 단위 → '12.15억'(억·소수 2자리) 한글 표기.
// 1억 미만은 '~만원'. (RankingComplexDetailModalV2 의 억/만원 한글 표기와 동일 톤)
const formatEokFromManwon = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  const abs = Math.abs(num);
  if (abs >= 10000) return `${(num / 10000).toFixed(2)}억`;
  return `${Math.round(num).toLocaleString('ko-KR')}만원`;
};

// axisPointer.label.formatter 는 { value } 객체를 넘겨줌 → 값 추출 유틸
const axisPointerValue = (params) => (params && params.value !== undefined ? params.value : params);

// 상단 가격 차트 ECharts 옵션 빌더 (순수 함수)
export function createTopOption({ filteredItems, colorByCode, metricVisible, syncedZoom, metricKeys, optionPrefix, chartTitle }) {
  const isSingleComplex = filteredItems.length === 1;
  const metricKeySet = new Set(metricKeys);
  const axis0Values = [];
  const axis1Values = [];
  const dateSet = new Set();
  // 최근8주 데이터가 있는 날짜 — 툴팁에서 해당 지점을 '최근8주'로 표기하기 위함
  const saleR8DateSet = new Set();
  const leaseR8DateSet = new Set();

  filteredItems.forEach((item) => {
    item.weeklySnapshots.forEach((row) => {
      if (row?.baseDate) dateSet.add(row.baseDate);
    });
    if (item?.topPriceDate) dateSet.add(item.topPriceDate);
    if (metricVisible.saleRecent8Weeks && metricKeySet.has('saleRecent8Weeks') && Array.isArray(item.saleRecent8Weeks)) {
      item.saleRecent8Weeks.forEach((row) => {
        const date = toDateKey(row?.baseDate);
        if (date) { dateSet.add(date); saleR8DateSet.add(date); }
      });
    }
    if (metricVisible.leaseRecent8Weeks && metricKeySet.has('leaseRecent8Weeks') && Array.isArray(item.leaseRecent8Weeks)) {
      item.leaseRecent8Weeks.forEach((row) => {
        const date = toDateKey(row?.baseDate);
        if (date) { dateSet.add(date); leaseR8DateSet.add(date); }
      });
    }
  });

  const dates = Array.from(dateSet).sort((a, b) => toTimestamp(a) - toTimestamp(b));
  const lineSeries = [];
  const starSeries = [];
  // 실거래가 라인 끝점에 "최근 실거래가 + 날짜" 라벨을 붙이기 위한 마커 시리즈(단지별 항상 표시)
  const realEndSeries = [];
  // 최근8주(호가) 별도 라인 시리즈 — 실거래/호가와 겹쳐서 함께 표기(채운 동그라미 + 시작점 점선)
  const recent8Series = [];
  // '2026-05-25' -> '26.05.25' (x축 라벨과 동일 포맷)
  const formatShortDate = (d) => {
    const s = String(d || '');
    return s.length >= 10 ? `${s.slice(2, 4)}.${s.slice(5, 7)}.${s.slice(8, 10)}` : s;
  };
  // 이 차트(variant)의 실거래가 지표 키 — 이 라인에만 끝점 라벨을 붙인다
  const realPriceKey = metricKeySet.has('saleRealPrice') ? 'saleRealPrice'
    : metricKeySet.has('leaseRealPrice') ? 'leaseRealPrice' : null;
  const hasRealEndLabel = !!realPriceKey && !!metricVisible[realPriceKey];

  filteredItems.forEach((item) => {
    const rowMap = new Map(item.weeklySnapshots.map((row) => [row.baseDate, row]));
    const color = colorByCode.get(item.compareKey ?? item.naverDanjiCode) || '#334155';
    const name = item.displayName || item.buildingName || item.naverDanjiCode;

    // 최근8주 토글이 켜져 있으면 그 구간의 가격(현재 표시 중인 호가/실거래) 데이터는 최근8주로 대체 → 해당 구간 미표시
    const saleR8Active = metricVisible.saleRecent8Weeks && metricKeySet.has('saleRecent8Weeks') && Array.isArray(item.saleRecent8Weeks) && item.saleRecent8Weeks.length > 0;
    const leaseR8Active = metricVisible.leaseRecent8Weeks && metricKeySet.has('leaseRecent8Weeks') && Array.isArray(item.leaseRecent8Weeks) && item.leaseRecent8Weeks.length > 0;
    const saleR8Start = saleR8Active ? recent8StartTs(item.saleRecent8Weeks) : Infinity;
    const leaseR8Start = leaseR8Active ? recent8StartTs(item.leaseRecent8Weeks) : Infinity;
    // 최근8주 값 맵(날짜→가격)
    const buildR8Map = (rows) => {
      const m = new Map();
      (rows || []).forEach((r) => {
        const d = toDateKey(r?.baseDate);
        const p = toNumber(r?.price);
        if (d && p != null) m.set(d, p);
      });
      return m;
    };
    const saleR8Map = saleR8Active ? buildR8Map(item.saleRecent8Weeks) : null;
    const leaseR8Map = leaseR8Active ? buildR8Map(item.leaseRecent8Weeks) : null;

    CHART_METRICS.forEach((metric) => {
      if (!metricKeySet.has(metric.key) || !metricVisible[metric.key]) return;
      const raw = dates.map((date) => {
        // 최근8주는 별도 라인으로 표기 → 실거래/호가 라인은 대체하지 않고 원래 값을 그대로 유지
        const row = rowMap.get(date);
        if (!row) return null;
        if (metric.key === 'leasePriceRate') {
          const salePrice = toNumber(row.salePrice);
          const leasePrice = toNumber(row.leasePrice);
          if (salePrice == null || leasePrice == null || salePrice <= 0) return null;
          return (leasePrice / salePrice) * 100;
        }
        return toNumber(row[metric.key]);
      });
      raw.forEach((value) => {
        if (value == null) return;
        if ((metric.yAxisIndex || 0) === 1) axis1Values.push(value);
        else axis0Values.push(value);
      });
      const seriesColor = isSingleComplex ? (METRIC_COLORS[metric.key] || color) : color;

      lineSeries.push({
        name: `${name} ${metric.label}`,
        type: 'line',
        connectNulls: true,
        // 호가/실거래가는 실선 + 실제 데이터 지점마다 속 빈 동그라미(raw: null 유지 → 데이터 있는 곳만 심볼)
        // showAllSymbol:true — 라인이 길어져도(실거래+최근8주 병합) 모든 데이터 지점(최근8주 포함)에 동그라미 강제
        showSymbol: !!metric.withSymbol,
        showAllSymbol: metric.withSymbol ? true : undefined,
        symbol: 'emptyCircle',
        symbolSize: metric.withSymbol ? 7 : 0,
        data: metric.withSymbol ? raw : fillContinuousValues(raw),
        lineStyle: {
          color: seriesColor,
          type: metric.dash,
          width: metric.width,
          opacity: 0.95,
          shadowColor: 'rgba(15, 23, 42, 0.06)',
          shadowBlur: 1,
        },
        itemStyle: { color: seriesColor, opacity: 1 },
        emphasis: { focus: 'series', lineStyle: { width: metric.width + 1.1, opacity: 1 }, itemStyle: { opacity: 1 } },
        blur: { lineStyle: { opacity: 0.12 }, itemStyle: { opacity: 0.18 } },
        // 동그라미 표시 라인(호가/실거래)은 샘플링 끔 — lttb가 경계 앵커/심볼을 버려 최근8주와 어긋나는 것 방지
        sampling: metric.withSymbol ? undefined : 'lttb',
        z: 5,
        yAxisIndex: metric.yAxisIndex || 0,
        // 실거래가 라인은 끝점 라벨(마커 시리즈)로 별도 표기 → 여기선 기본 endLabel 생략
        endLabel: (isSingleComplex && metric.key !== realPriceKey) ? getEndLabelStyle(metric.label, seriesColor) : undefined,
        labelLayout: (isSingleComplex && metric.key !== realPriceKey) ? { moveOverlap: 'shiftY' } : undefined,
      });

      // 실거래가 라인: 제일 끝(가장 최근 실거래) 지점에 색상 박스테두리 라벨(가격 + 우상단 날짜)
      if (metric.key === realPriceKey) {
        let lastIdx = -1;
        for (let i = raw.length - 1; i >= 0; i -= 1) { if (raw[i] != null) { lastIdx = i; break; } }
        if (lastIdx >= 0) {
          const lastDate = dates[lastIdx];
          const lastPrice = raw[lastIdx];
          const dateLabel = formatShortDate(lastDate);
          const priceLabel = formatKrwEokFromManwon(lastPrice);
          realEndSeries.push({
            name: `${name} 최근실거래`,
            type: 'scatter',
            data: [[lastDate, lastPrice]],
            symbolSize: 6,
            // 박스테두리/텍스트는 '해당 단지 색상'으로 고정
            itemStyle: { color, borderColor: '#ffffff', borderWidth: 1.2 },
            silent: true,
            tooltip: { show: false },
            yAxisIndex: metric.yAxisIndex || 0,
            z: 30,
            zlevel: 2,
            label: {
              show: true,
              position: 'right',
              distance: 9,
              formatter: () => `{d|${dateLabel}}\n{p|${priceLabel}}`,
              backgroundColor: 'rgba(255,255,255,0.96)',
              borderColor: color,
              borderWidth: 1.4,
              borderRadius: 5,
              padding: [3, 7, 4, 7],
              lineHeight: 13,
              rich: {
                d: { align: 'right', fontSize: 9, color: '#94a3b8', fontWeight: 600 },
                p: { align: 'center', fontSize: 12, fontWeight: 800, color },
              },
            },
            labelLayout: { moveOverlap: 'shiftY' },
          });
        }
      }
    });

    // 최근8주(호가) 별도 라인 — 실거래/호가와 겹쳐 표기(채운 동그라미), 시작점에 점선 세로선
    const buildRecent8Series = (active, r8Map, startTs, metricLabel, metricKey) => {
      if (!active || !r8Map || r8Map.size === 0) return;
      const r8Color = isSingleComplex ? (METRIC_COLORS[metricKey] || color) : color;
      const r8Data = dates.map((date) => {
        if (toTimestamp(date) < startTs) return null;
        const v = r8Map.get(date);
        return v != null ? v : null;
      });
      r8Data.forEach((v) => { if (v != null) axis0Values.push(v); });
      const startDate = [...r8Map.keys()].sort((a, b) => toTimestamp(a) - toTimestamp(b))[0];
      // 여러 최근8주 말풍선이 같은 시작일에 겹치지 않도록 세로 순번(→ 아래로 stagger)
      const r8LabelIndex = recent8Series.length;
      recent8Series.push({
        name: `${name} ${metricLabel}`,
        type: 'line',
        connectNulls: true,
        showSymbol: true,
        showAllSymbol: true,
        symbol: 'circle', // 채운 동그라미 (실거래=속빈 동그라미와 구분)
        symbolSize: 7,
        data: r8Data,
        lineStyle: { color: r8Color, width: 2, type: 'solid', opacity: 0.95 },
        itemStyle: { color: r8Color },
        emphasis: { focus: 'series' },
        z: 6,
        yAxisIndex: 0,
        endLabel: isSingleComplex ? getEndLabelStyle(metricLabel, r8Color) : undefined,
        labelLayout: isSingleComplex ? { moveOverlap: 'shiftY' } : undefined,
        markLine: startDate ? {
          symbol: 'none',
          silent: true,
          lineStyle: { type: 'dashed', color: r8Color, width: 1.2, opacity: 0.9 },
          // 세로선 하단(날짜축 쪽) 조금 아래에 "단지명 최근8주" 말풍선
          label: {
            show: true,
            formatter: `${name} 최근8주`,
            position: 'start',
            // 날짜축보다 더 아래로. 여러 개면 위아래로 겹치지 않게 순번만큼 더 내림.
            distance: 22 + r8LabelIndex * 18,
            color: r8Color,
            fontSize: 10,
            fontWeight: 700,
            backgroundColor: '#ffffff',
            borderColor: r8Color,
            borderWidth: 1,
            borderRadius: 6,
            padding: [3, 7],
            shadowColor: 'rgba(15,23,42,0.20)',
            shadowBlur: 5,
          },
          data: [{ xAxis: startDate }],
        } : undefined,
      });
    };
    buildRecent8Series(saleR8Active, saleR8Map, saleR8Start, '매매 최근8주', 'saleRecent8Weeks');
    buildRecent8Series(leaseR8Active, leaseR8Map, leaseR8Start, '전세 최근8주', 'leaseRecent8Weeks');

    if (metricVisible.topPrice && dates.length > 0 && item.topPrice != null) {
      const topDate = toDateKey(item.topPriceDate);
      const targetDate = topDate || dates[dates.length - 1];
      axis0Values.push(item.topPrice);
      const starColor = isSingleComplex ? (METRIC_COLORS.topPrice || color) : color;
      const borderColor = darkenHex(starColor, 0.45);
      starSeries.push({
        name: `${name} 최고가`,
        type: 'scatter',
        symbol: STAR_SYMBOL_PATH,
        symbolSize: 22,
        symbolKeepAspect: true,
        data: [[targetDate, item.topPrice, topDate || null, item.topPriceDealType ?? null]],
        itemStyle: { color: starColor, borderColor, borderWidth: 2.8, shadowColor: borderColor, shadowBlur: 10, opacity: 0.95 },
        emphasis: { scale: true, itemStyle: { color: starColor, borderColor: darkenHex(starColor, 0.6), borderWidth: 3.2 } },
        blur: { itemStyle: { opacity: 0.2 } },
        z: 50,
        zlevel: 3,
        label: isSingleComplex
          ? { show: true, formatter: '최고가', position: 'top', color: borderColor, fontSize: 11, fontWeight: 800 }
          : undefined,
      });
    }

    // 최근8주는 위 활성 가격 라인에 병합되어 이어붙었으므로 별도 라인을 만들지 않는다.
  });

  const series = [...lineSeries, ...recent8Series, ...realEndSeries, ...starSeries];
  const axis0Range = buildPaddedAxisRange(axis0Values, { clampMinZero: true, minSpanRatio: 0.1, padRatio: 0.14 });
  const axis1RangeBase = buildPaddedAxisRange(axis1Values, { clampMinZero: false, minSpanRatio: 0.12, padRatio: 0.16 });
  // 전세가율(우측축) 라인을 플롯 하단으로 내려 매매가/실거래 라인과 겹치지 않게:
  // 축 max 를 크게 확장 → 같은 값들이 아래쪽에 매핑됨.
  const axis1Range = (axis1RangeBase.min != null && axis1RangeBase.max != null)
    ? { ...axis1RangeBase, max: axis1RangeBase.max + (axis1RangeBase.max - axis1RangeBase.min) * 1.0 }
    : axis1RangeBase;

  // dash 값(문자열/배열) → 범례 라인 그리기용 lineDash 배열
  const toLineDash = (dash) => (Array.isArray(dash) ? dash : (dash === 'solid' ? undefined : [6, 4]));

  // 다중 단지: 색 = 단지 이므로 라인샘플은 중립색(회색)으로 → "라인종류=항목" 임을 분명히.
  // 단일 단지: 색 = 항목 이므로 항목색 사용.
  const legendLineColor = (metricKey) => (isSingleComplex ? (METRIC_COLORS[metricKey] || '#334155') : '#64748b');

  // 텍스트 픽셀 폭 추정(한글 ~13 / 기타 ~7 @13px) — 범례 항목 간격 계산용
  const estTextWidth = (label) => {
    let w = 0;
    for (const ch of String(label || '')) w += /[가-힣ㄱ-ㆎ]/.test(ch) ? 13 : 7;
    return w;
  };

  // 항목(라인 종류) 범례 — 라인샘플 + 항목명(설명 텍스트 제거로 깔끔하게)
  const guideItems = CHART_METRICS
    .filter((metric) => metricKeySet.has(metric.key) && metricVisible[metric.key])
    .map((metric) => ({
      label: metric.label,
      color: legendLineColor(metric.key),
      dash: metric.dash,
      width: metric.width,
    }));

  const LGD_ICON = 26;
  const LGD_GAP = 7;
  const LGD_ITEM_GAP = 22;
  const LGD_FONT = 13;
  let guideOffsetX = 0;
  const guideLayouts = guideItems.map((item) => {
    const layout = { ...item, x: guideOffsetX };
    guideOffsetX += LGD_ICON + LGD_GAP + estTextWidth(item.label) + LGD_ITEM_GAP;
    return layout;
  });
  // 범례 — 차트 정중앙 상단, 라인샘플과 텍스트 수직 중앙 정렬
  const metricLegendGraphic = {
    type: 'group',
    left: 'center',
    top: 10,
    silent: true,
    children: guideLayouts.flatMap((item) => ([
      {
        type: 'line',
        shape: { x1: item.x, y1: 8, x2: item.x + LGD_ICON, y2: 8 },
        style: { stroke: item.color, lineWidth: Math.max(2.4, item.width || 2.4), lineDash: toLineDash(item.dash) },
      },
      {
        type: 'text',
        style: { x: item.x + LGD_ICON + LGD_GAP, y: 8, text: item.label, textVerticalAlign: 'middle', fill: '#475569', font: `600 ${LGD_FONT}px sans-serif` },
      },
    ])),
  };

  // 단지 색 구분은 차트 위 HTML 토글바에서 표기(겹침 방지) → 차트 안에는 항목 라인종류 범례만.

  return {
    animationDuration: 360,
    // 제목은 HTML h4(ChartCard)로 렌더. 범례는 상단 중앙(아래 metricLegendGraphic).
    grid: { left: 56, right: (isSingleComplex || hasRealEndLabel) ? 132 : 40, top: 44, bottom: 52 + (recent8Series.length > 0 ? 30 + (recent8Series.length - 1) * 18 : 0) },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross' },
      backgroundColor: 'rgba(255,255,255,0.98)',
      borderColor: '#e2e8f0',
      borderWidth: 1,
      textStyle: { color: '#0f172a' },
      extraCssText: 'box-shadow: 0 8px 24px rgba(15,23,42,.12);',
      // 기본 마크업의 addCommas 경로 우회 (스택 오버플로 방지)
      valueFormatter: safeTooltipValueFormatter,
      formatter: (params) => {
        if (!Array.isArray(params) || params.length === 0) return '';
        const date = params[0]?.axisValue || '';
        const groups = new Map();
        const labelPattern = new RegExp(` (${METRIC_TOGGLES.map((m) => m.label).join('|')})$`);

        params.forEach((param) => {
          const rawName = String(param.seriesName || '');
          const value = Array.isArray(param.value) ? param.value[1] : param.value;
          if (value == null) return;

          let matchedMetric = '기타';
          for (const metric of METRIC_TOGGLES) {
            if (rawName.endsWith(` ${metric.label}`)) { matchedMetric = metric.label; break; }
          }
          // 최근8주는 별도 시리즈('매매/전세 최근8주')로 표기되므로 실거래/호가 재라벨 안 함
          const complexName = rawName.replace(labelPattern, '');
          const isTop = matchedMetric === '최고가' && Array.isArray(param.value);
          if (!groups.has(complexName)) groups.set(complexName, []);
          groups.get(complexName).push({
            metric: matchedMetric,
            value,
            color: param.color,
            topTradeDate: isTop ? param.value[2] : null,
            topDealType: isTop ? param.value[3] : null,
          });
        });

        let html = `<div style="font-weight:700;margin-bottom:6px;">${date}</div>`;
        groups.forEach((rows, complexName) => {
          html += `<div style="margin:6px 0 4px;font-weight:700;color:#0f172a;">${complexName}</div>`;
          rows.forEach((row) => {
            html += `<div style="display:flex;align-items:center;gap:6px;margin:2px 0 2px 2px;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${row.color};"></span>
              <span style="color:#475569;">${row.metric}</span>
              <span style="margin-left:auto;color:#0f172a;font-weight:700;">${row.metric === '전세가율' ? formatPercent(row.value) : formatKrwEokFromManwon(row.value)}</span>
            </div>`;
            if (row.metric === '최고가' && row.topTradeDate) {
              html += `<div style="margin:0 0 4px 16px;font-size:11px;color:#6b7280;">거래일자 ${row.topTradeDate}</div>`;
            }
            if (row.metric === '최고가' && row.topDealType != null) {
              html += `<div style="margin:0 0 4px 16px;font-size:11px;color:#6b7280;">거래유형 ${getDealTypeLabel(row.topDealType)}</div>`;
            }
          });
        });
        return html;
      },
    },
    axisPointer: { link: [{ xAxisIndex: 'all' }] },
    legend: { show: false },
    graphic: [metricLegendGraphic],
    xAxis: {
      type: 'category',
      data: dates,
      boundaryGap: false,
      axisLabel: {
        formatter: (value) => {
          const text = String(value || '');
          return text.length >= 10 ? `${text.slice(2, 4)}.${text.slice(5, 7)}.${text.slice(8, 10)}` : text;
        },
        color: '#64748b',
      },
      axisLine: { lineStyle: { color: '#cbd5e1' } },
    },
    yAxis: [
      {
        type: 'value',
        ...axis0Range,
        axisLabel: { formatter: (value) => formatKrwInManWonShort(value), color: '#64748b' },
        // 마우스 크로스헤어 값: 12.15억(억·소수 2자리) 한글 표기
        axisPointer: { label: { formatter: (params) => formatEokFromManwon(axisPointerValue(params)) } },
        splitLine: { lineStyle: { color: '#e2e8f0' } },
      },
      {
        type: 'value',
        position: 'right',
        ...axis1Range,
        show: false,
        axisLabel: { formatter: (value) => `${Number(value).toFixed(0)}%`, color: '#0369a1' },
        // 전세가율 크로스헤어 값: 소수 1자리
        axisPointer: { label: { formatter: (params) => `${Number(axisPointerValue(params)).toFixed(1)}%` } },
        splitLine: { show: false },
      },
    ],
    toolbox: { show: false }, // 영역지정(브러시) 버튼 등 툴박스 UI 숨김
    brush: {
      // 버튼 없이 takeGlobalCursor 로만 활성화(자동 on)
      toolbox: ['lineX', 'clear'],
      xAxisIndex: 0,
      brushStyle: { borderWidth: 2, color: 'rgba(59, 130, 246, 0.2)', borderColor: 'rgba(59, 130, 246, 0.8)' },
      outOfBrush: { colorAlpha: 0.4 },
    },
    dataZoom: buildSyncedDataZoom(syncedZoom, optionPrefix),
    series,
  };
}

// 상단 가격 차트 컴포넌트
export default function TopChart({
  variant, // 'sale' | 'lease'
  filteredItems,
  colorByCode,
  metricVisible,
  syncedZoom,
  brushRangeRows,
  onToggle,
  onBrushClose,
  chartRef,
  chartEvents,
  height,
}) {
  // 최근8주는 '단지별 보기'에서만 노출. 통합 차트(TopChart)에 단지가 2개 이상 겹치면 숨긴다.
  // (단일 단지 통합 차트에서는 그대로 표시)
  const hideRecent8 = (Array.isArray(filteredItems) ? filteredItems.length : 0) > 1;
  const effectiveMetricVisible = useMemo(() => (
    hideRecent8
      ? { ...metricVisible, saleRecent8Weeks: false, leaseRecent8Weeks: false }
      : metricVisible
  ), [metricVisible, hideRecent8]);

  const option = useMemo(
    () => createTopOption({
      filteredItems,
      colorByCode,
      metricVisible: effectiveMetricVisible,
      syncedZoom,
      metricKeys: MAIN_METRICS[variant],
      optionPrefix: TOP_OPTION_PREFIX[variant],
    }),
    [filteredItems, colorByCode, effectiveMetricVisible, syncedZoom, variant],
  );

  return (
    <ChartCard
      panelPrefix={`ranking-compare-combined-${variant}-top`}
      title={variant === 'sale' ? '매매 가격' : '전세 가격'}
      brushTitle={TOP_BRUSH[variant].title}
      brushMetrics={TOP_BRUSH[variant].metrics}
      brushRangeRows={brushRangeRows}
      metricVisible={effectiveMetricVisible}
      onBrushClose={onBrushClose}
      toggleKeys={TOP_TOGGLE_KEYS[variant]}
      toggleGroups={buildTopToggleGroups(variant, effectiveMetricVisible, hideRecent8)}
      onToggle={onToggle}
      option={option}
      chartRef={chartRef}
      height={height}
      chartEvents={chartEvents}
    />
  );
}
